/**
 * **The trample crossing sweep** — the engine-side derivation that turns "a Trampling mover just
 * travelled this path" into the set of CHARACTERS that path crossed. Pure: reads state, returns
 * ids, applies nothing. The caller (`referee/moveFollowups.ts`'s `trampleInto`) fires the
 * `"trample"` trigger (`resolve/triggers.ts`'s `resolveTrampleTrigger`) once per id this returns,
 * which is where the actual damage/effects live — authored content, not engine code (Phase 3.4's
 * `{TIER}` binding is exactly why: the magnitude must resolve at INTERPRET time, off the
 * authored clause, never a literal baked in here).
 *
 * **Structural precedent: `./bulldoze.ts`'s `bulldozeSweep`** — same swept-footprint-over-path
 * derivation — but deliberately NARROWER in two ways that mirror {@link
 * "@lancer-vtt/domain".TRAMPLING_STATUS}'s own doc:
 *  1. **Characters only.** `bulldozeSweep` is kind-agnostic (objects die to the same run-over as
 *     characters); Trample's RAW ("passes through occupied SPACES," Core p.297) and this
 *     project's own Q4 scope stop at characters — an object/deployable in a Trampling mover's
 *     path is untouched by this sweep (it's still a movement OBSTRUCTION per
 *     `classifyOccupant`'s object-kind tier, just never a trample damage target).
 *  2. **No damage, no terrain change, no dice roll here at all.** Unlike `bulldozeSweep` (which
 *     rolls 2d6 once and emits the full `DamageStep`/`ForcedMoveStep` chain itself), this module
 *     only enumerates WHO was crossed — the damage magnitude is authored (`{TIER}`-scaled,
 *     Phase 3.4) and resolved per crossed character by the fired `"trample"` clause, not rolled
 *     once here and dealt uniformly the way the rig's run-over is.
 */
import { TRAMPLING_STATUS, charactersCrossed, hasStatus, type Cell, type CombatantId, type EncounterState } from "@lancer-vtt/domain";

/**
 * Whether `combatantId` is mid-trample — the one gate every caller checks before paying for a
 * sweep. Exported so the followup seam can early-out without constructing anything, mirroring
 * `bulldoze.ts`'s `isTerraforming`.
 */
export function isTrampling(state: EncounterState, combatantId: CombatantId): boolean {
  const c = state.combatants[combatantId];
  return c !== undefined && hasStatus(c.statuses, TRAMPLING_STATUS);
}

/**
 * The distinct CHARACTER ids (`{@link "@lancer-vtt/domain".isCharacter}` — mech/npc/pilot/drone,
 * RAW's own "tech-targetable, makes saves" lane, the canonical predicate an AoE-style sweep like
 * this one gates on) whose occupied cell `moverId`'s committed `path` crossed, `from` and `to`
 * inclusive — objects/deployables and terrain are never included (see this module's own doc,
 * item 1). Deterministic id order (sorted), mirroring `bulldozeSweep`'s own sweep so a replay's
 * per-target fire order is reproducible. Returns `[]` for a placement/no-op path (`path.length <
 * 2`) or a since-removed mover — the caller's own no-op case, mirroring `bulldozeSweep`'s guard.
 *
 * **Does NOT check {@link isTrampling}** — a pure geometry query, deliberately decoupled from
 * the status gate so a caller (or a test) can ask "what would this path cross" independent of
 * whether the mover currently carries the marker. `trampleInto` (`referee/moveFollowups.ts`)
 * checks `isTrampling` itself before calling this. It is `@lancer-vtt/domain`'s
 * `charactersCrossed`, the same set a Trampler's default route avoids.
 */
export function trampledCharacterIds(args: { state: EncounterState; moverId: CombatantId; path: readonly Cell[] }): CombatantId[] {
  return charactersCrossed(args.state, args.moverId, args.path);
}
